package com.steamstreet.vegasful.browser.account.intelligence

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.browser.account.initEmailCapture
import com.steamstreet.vegasful.browser.account.loginOptions
import com.steamstreet.vegasful.browser.account.subscriptions.EntityInteractionsController
import com.steamstreet.vegasful.browser.components.NeonSky
import com.steamstreet.vegasful.browser.components.autoGrow
import com.steamstreet.vegasful.browser.components.isTonight
import com.steamstreet.vegasful.browser.components.typewriterPlaceholders
import com.steamstreet.vegasful.browser.components.vegasNow
import com.steamstreet.vegasful.graphql.api.ConversationInput
import com.steamstreet.vegasful.graphql.api.GPSCoordinatesInput
import com.steamstreet.vegasful.web.ClientController
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.dom.create
import org.w3c.dom.Element
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.asList
import org.w3c.dom.events.KeyboardEvent
import kotlin.coroutines.resume
import kotlin.js.Promise
import kotlin.js.json

/**
 * How long a single turn is polled before the browser gives up on it.
 *
 * A conversation runs in the server process rather than on a durable queue, so a task replaced
 * mid-answer -- which any deploy does -- leaves its record RUNNING until the record expires. The
 * poll has to end itself, or the page sits asking about a dead turn once a second indefinitely.
 */
private const val maxPollAttempts: Int = 180

/** The `context` every failure from this feature is reported under. */
private const val INTELLIGENCE_CONTEXT: String = "intelligence"

/**
 * The stages a turn reports while it runs, in order, matching the server's `data-stage` values.
 * Each has what it says while it is the current stage, and what it says before it starts.
 */
private class Stage(val key: String, val word: String, val waiting: String, val working: String, val finished: String)

private val STAGES = listOf(
    Stage("reading", "Reading", "What you're after", "Working out when, where and what kind of night", "Got it"),
    Stage("searching", "Searching", "Everything that fits", "Looking through everything that's on", "Found the close matches"),
    Stage("choosing", "Choosing", "The best, and why", "Weighing each one and writing why it fits", "Done")
)

/**
 * The concierge page (`intelligence`).
 *
 * The server renders the desk: the sky's canvas, the headline, the box and the example questions,
 * and an empty thread below. This module brings the sky to life, keeps the headline's neon word
 * on the Las Vegas hour, and asks the concierge. Each question becomes a turn in the
 * thread: the question, then a working panel whose three neon words light up as the server reports
 * each stage, then the answer, which is the server's `/conversations/{id}` snippet. Every question
 * after the first is asked against the same conversation, so it reads as a follow-up.
 *
 * Questions are sent exactly as typed. A visitor who wants a particular night says so ("What's
 * happening tonight?"); the concierge reads the time frame from the question and the thread.
 *
 * A visitor who is not signed in can type a question. Asking it holds the question with
 * [ConciergeHandoff] and shows the sign-in options; the question is asked once they are back.
 */
class Intelligence(private val element: Element, private val scope: CoroutineScope) : ClientController {
    private val root = element as HTMLElement
    private val httpClient = HttpClient()

    private val desk = root.querySelector(".cg-desk") as? HTMLElement
    private val thread = root.querySelector(".cg-thread") as? HTMLElement
    private val input = root.querySelector(".cg-desk .cg-input") as? HTMLTextAreaElement
    private val askButton = root.querySelector(".cg-desk .cg-ask") as? HTMLButtonElement
    private val neonWord = root.querySelector(".cg-neon") as? HTMLElement
    private val sky = desk?.let { d -> (d.querySelector(".cg-sky") as? HTMLCanvasElement)?.let { NeonSky(d, it) } }
    private val reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    /** The thread being added to, once the first question has opened one. */
    private var conversationId: String? = null

    /** Whether a turn is running. Only one runs at a time, so each follow-up has an answer to follow. */
    private var busy = false

    override suspend fun run() {
        root.classList.add("cg-ready")
        sky?.start()
        root.querySelectorAll(".cg-tube").asList().forEach { fitTube(it as HTMLElement) }

        drawNeonWord()
        // "Tonight" becomes true during the afternoon, so the word is checked again.
        window.setInterval({ drawNeonWord() }, 60_000)

        input?.let { field ->
            field.autoGrow(1, 5)
            field.typewriterPlaceholders(scope, ConciergeHandoff.EXAMPLE_QUESTIONS)
            submitOnEnter(field)
        }
        askButton?.addEventListener("click", { ask(input) })
        root.querySelectorAll(".cg-desk .cg-chip").asList().forEach { chip ->
            chip.addEventListener("click", { ask(chip.textContent) })
        }

        // A question asked from the home page, or here before signing in, arrives held. It is
        // asked once the visitor is signed in; until then it waits behind the sign-in options.
        if (Account.isLoggedIn()) {
            ConciergeHandoff.take()?.let { held -> scope.launch { executeQuery(held) } }
        } else {
            ConciergeHandoff.peek()?.let { showSignIn(it) }
        }
    }

    // ---------------------------------------------------------------- the desk

    /**
     * The headline's neon word: "Where to, tonight?" in the evening and "Where to, today?" before,
     * by the Las Vegas clock. It is only a greeting; nothing is added to the question.
     */
    private fun drawNeonWord() {
        val word = if (isTonight(vegasNow())) "tonight" else "today"
        if (neonWord?.textContent != word) neonWord?.textContent = word
    }

    /** Fit a neon tube's rectangles to its box, now and whenever the box changes size. */
    private fun fitTube(tube: HTMLElement) {
        fun fit() {
            val svg = tube.querySelector(".cg-ring") ?: return
            val rect = tube.getBoundingClientRect()
            val w = rect.width + 12
            val h = rect.height + 12
            svg.setAttribute("viewBox", "0 0 $w $h")
            svg.querySelectorAll("rect").asList().forEach {
                (it as Element).apply {
                    setAttribute("x", "2")
                    setAttribute("y", "2")
                    setAttribute("width", "${maxOf(1.0, w - 4)}")
                    setAttribute("height", "${maxOf(1.0, h - 4)}")
                    setAttribute("rx", "19")
                }
            }
        }
        fit()
        val resizeObserver = window.asDynamic().ResizeObserver
        if (resizeObserver != undefined) {
            js("Reflect").construct(resizeObserver, arrayOf<dynamic>({ _: dynamic -> fit() })).observe(tube)
        }
    }

    private fun submitOnEnter(field: HTMLTextAreaElement) {
        field.addEventListener("keydown", { event ->
            val key = event as KeyboardEvent
            if (key.key == "Enter" && !key.shiftKey && !key.isComposing) {
                event.preventDefault()
                ask(field)
            }
        })
    }

    private fun ask(field: HTMLTextAreaElement?) {
        val text = field?.value?.trim().orEmpty()
        if (text.isEmpty()) {
            field?.focus()
            return
        }
        if (ask(text)) field?.value = ""
    }

    /** Ask [text], if nothing is running. Returns whether it was taken. */
    private fun ask(text: String?): Boolean {
        val question = text?.trim()?.takeIf { it.isNotEmpty() } ?: return false
        if (busy) return false
        if (!Account.isLoggedIn()) {
            ConciergeHandoff.hold(question)
            showSignIn(question)
            return true
        }
        askButton?.let { sky?.burst(it) }
        scope.launch { executeQuery(question) }
        return true
    }

    private fun setBusy(value: Boolean) {
        busy = value
        root.classList.toggle("cg-busy", value)
        sky?.excited = value
        root.querySelectorAll(".cg-ask, .cg-chip").asList().forEach {
            (it as? HTMLButtonElement)?.disabled = value
        }
        askButton?.querySelector("span")?.textContent = if (value) "Working" else "Ask"
    }

    // ---------------------------------------------------------------- a turn

    /** Add a turn to the thread for [query] and return the element its answer goes in. */
    private fun startTurn(query: String): HTMLElement? {
        val thread = thread ?: return null
        // Only the latest answer offers a follow-up box; the main box above always works too.
        thread.querySelectorAll(".cg-followup").asList().forEach { (it as Element).remove() }
        val turn = document.create.div("cg-turn") {
            div("cg-asked") {
                span("cg-asked-label") { +"You asked" }
                p("cg-asked-q") { +"“$query”" }
            }
            div("cg-slot") {}
        }
        thread.appendChild(turn)
        window.requestAnimationFrame {
            turn.asDynamic().scrollIntoView(json("behavior" to if (reducedMotion) "auto" else "smooth", "block" to "start"))
        }
        return turn.querySelector(".cg-slot") as? HTMLElement
    }

    private fun showSignIn(question: String) {
        val thread = thread ?: return
        thread.innerHTML = ""
        val slot = startTurn(question) ?: return
        slot.append {
            div("cg-signin") {
                h2 { +"Sign in and I'll answer that" }
                p { +"The concierge is for Vegasful members. It's free, and your question will be waiting when you're back." }
                div("cg-signin-options") {
                    loginOptions()
                }
            }
        }
        initEmailCapture(slot, "concierge")
    }

    private suspend fun executeQuery(query: String) {
        // The question itself never leaves the browser: only how long it was. Intelligence
        // questions are free text about what somebody wants to do on a given night, which is
        // exactly the sort of thing `docs/analytics-plan.md` keeps out of the analytics stream.
        Analytics.track(
            AnalyticsEvent.INTELLIGENCE_QUERIED,
            AnalyticsParam.QUERY_LENGTH to query.length
        )

        val slot = startTurn(query) ?: return
        val work = workPanel(slot)
        setBusy(true)
        // Once a question is asked, the desk's headline, box and examples have done their job and
        // only push the answer down. The desk folds to a band of sky, and the follow-up box under
        // each answer is where the next question is asked.
        root.classList.add("cg-started")
        val clock = startClock(work)

        try {
            val location = grantedLocation()
            val id = GraphQL.client {
                conversations {
                    start(ConversationInput(query, location, conversationId)) {
                        id
                    }
                }
            }.conversations.start.id

            // Every later question in this thread is sent against the same id, which is what
            // lets the server read "something earlier" as a refinement rather than a fresh
            // search.
            conversationId = id
            pollForConversation(id, slot, work)
        } catch (e: Exception) {
            // Polling handles its own failures; this is the conversation failing to start at
            // all. Without it the thrown exception would escape into the launching scope and
            // leave the box disabled, with no way to ask anything again.
            console.error("Unable to start the conversation:", e)
            showError(slot, "I couldn't start on that. Please ask again.")
            Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation start failed")
            reportResponded(success = false)
        } finally {
            clock.cancel()
            setBusy(false)
        }
    }

    /** The panel shown while a turn runs: the three stages, a running clock, and what to expect. */
    private fun workPanel(slot: HTMLElement): HTMLElement {
        slot.append {
            div("cg-work") {
                attributes["role"] = "status"
                div("cg-stages") {
                    STAGES.forEach { stage ->
                        div("cg-stage") {
                            attributes["data-stage"] = stage.key
                            div("cg-stage-word") { +stage.word }
                            div("cg-stage-bar") { i {} }
                            div("cg-stage-detail") { +stage.waiting }
                        }
                    }
                }
                div("cg-work-foot") {
                    span("cg-clock") { +"0:00" }
                    span { +"Good answers take about a minute. You can keep this tab open and come back." }
                }
            }
        }
        val work = slot.querySelector(".cg-work") as HTMLElement
        showStage(work, STAGES.first().key)
        return work
    }

    /** Light the stage named [key], and every stage before it as finished. */
    private fun showStage(work: HTMLElement, key: String?) {
        val current = STAGES.indexOfFirst { it.key == key }.coerceAtLeast(0)
        STAGES.forEachIndexed { index, stage ->
            val el = work.querySelector(".cg-stage[data-stage=${stage.key}]") as? HTMLElement ?: return@forEachIndexed
            val detail = el.querySelector(".cg-stage-detail")
            when {
                index < current -> if (!el.classList.contains("done")) {
                    el.classList.remove("on")
                    el.classList.add("done")
                    detail?.textContent = stage.finished
                }
                // Added only once, so the buzz and the slow fill of the bar are not restarted by
                // each poll that reports the same stage.
                index == current -> if (!el.classList.contains("on")) {
                    el.classList.add("on")
                    detail?.textContent = stage.working
                }
            }
        }
    }

    private fun startClock(work: HTMLElement): Job {
        val started = window.performance.now()
        val clock = work.querySelector(".cg-clock")
        return scope.launch {
            while (isActive) {
                val seconds = ((window.performance.now() - started) / 1000).toInt()
                clock?.textContent = "${seconds / 60}:${(seconds % 60).toString().padStart(2, '0')}"
                delay(500)
            }
        }
    }

    private suspend fun pollForConversation(id: String, slot: HTMLElement, work: HTMLElement) {
        var attempts = 0
        try {
            while (true) {
                delay(1000)

                attempts += 1
                if (attempts > maxPollAttempts) {
                    showError(slot, "This is taking far longer than it should. Please ask again.")
                    Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation timed out")
                    reportResponded(success = false)
                    break
                }

                val response = httpClient.get("/conversations/$id")
                if (!response.status.isSuccess()) {
                    console.error("HTTP error: ${response.status.value} ${response.status.description}")
                    showError(slot, "Something went wrong while I was looking. Please ask again.")
                    Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation poll failed", response.status.value)
                    reportResponded(success = false)
                    break
                }

                val html = response.bodyAsText()

                // While the turn runs, the snippet only names the stage it has reached.
                if (html.contains("conversation-status")) {
                    val parsed = document.createElement("div")
                    parsed.innerHTML = html
                    showStage(work, parsed.querySelector(".conversation-status")?.getAttribute("data-stage"))
                    continue
                }

                // The snippet is now the answer, or a note saying why there is none.
                slot.innerHTML = html
                val failed = slot.querySelector(".cg-error") != null
                if (failed) {
                    addFollowUp(slot, null)
                } else {
                    slot.classList.add("cg-dealing")
                    wireAnswer(slot)
                }
                reportResponded(success = !failed)
                break
            }
        } catch (e: Exception) {
            console.error("Error polling conversation:", e)
            showError(slot, "Something went wrong while I was looking. Please ask again.")
            Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation polling threw")
            reportResponded(success = false)
        }
    }

    private fun showError(slot: HTMLElement, message: String) {
        slot.innerHTML = ""
        slot.append {
            div("cg-error") {
                div("cg-note") {
                    p { +message }
                }
            }
        }
        addFollowUp(slot, null)
    }

    // ---------------------------------------------------------------- the answer

    /**
     * Make an inserted answer work: the save hearts, the "Read more" on a reason that was cut
     * short, and the follow-up box.
     */
    private fun wireAnswer(slot: HTMLElement) {
        slot.querySelectorAll("[data-entity-interactions]").asList().forEach { node ->
            val control = node as Element
            scope.launch { EntityInteractionsController(control, scope).run() }
        }

        // A reason is clamped to four lines in the grid. The button only appears for one that
        // was actually cut short, which can only be known once it is laid out.
        window.requestAnimationFrame {
            slot.querySelectorAll(".cg-more").asList().forEach { node ->
                val button = node as HTMLElement
                val pick = button.closest(".cg-pick") ?: return@forEach
                val reason = pick.querySelector(".cg-why span") as? HTMLElement ?: return@forEach
                button.hidden = reason.scrollHeight <= reason.clientHeight + 2
                button.addEventListener("click", {
                    val open = pick.classList.toggle("open")
                    button.textContent = if (open) "Show less" else "Read more"
                })
            }
        }

        addFollowUp(slot, slot.querySelector(".cg-pick .cg-pick-name")?.textContent?.trim())
    }

    /**
     * The box for the next question, under the latest answer. With the desk folded away it is the
     * only place to ask, so every turn gets one, including a turn that found nothing or failed.
     * Refinement chips are offered only when there are picks to refine.
     */
    private fun addFollowUp(slot: HTMLElement, topPick: String?) {
        val refinements = topPick?.let {
            listOf("Earlier in the evening", "Only the free ones", "Somewhere on the Strip", "More like $it")
        }.orEmpty()
        slot.append {
            div("cg-followup") {
                h3 { +if (topPick != null) "Not quite it? Tell me more." else "Try asking another way." }
                if (refinements.isNotEmpty()) {
                    div("cg-tries") {
                        refinements.forEach {
                            button(classes = "cg-chip") {
                                type = ButtonType.button
                                +it
                            }
                        }
                    }
                }
                div("cg-tube") {
                    unsafe {
                        +"""<svg class="cg-ring" aria-hidden="true"><rect class="tube" pathLength="100"></rect><rect class="core" pathLength="100"></rect><rect class="chase" pathLength="100"></rect></svg>"""
                    }
                    div("cg-box") {
                        textArea(classes = "cg-input") {
                            rows = "1"
                            placeholder = "Ask a follow-up…"
                            attributes["aria-label"] = "Ask a follow-up"
                            attributes["enterkeyhint"] = "send"
                        }
                        button(classes = "cg-ask") {
                            type = ButtonType.button
                            span { +"Ask" }
                        }
                    }
                }
            }
        }
        val box = slot.querySelector(".cg-followup") as? HTMLElement ?: return
        box.querySelectorAll(".cg-chip").asList().forEach { chip ->
            chip.addEventListener("click", { ask(chip.textContent) })
        }
        val field = box.querySelector(".cg-input") as? HTMLTextAreaElement
        field?.autoGrow(1, 5)
        field?.let { submitOnEnter(it) }
        box.querySelector(".cg-ask")?.addEventListener("click", { ask(field) })
        (box.querySelector(".cg-tube") as? HTMLElement)?.let { fitTube(it) }
    }

    /**
     * The user's coordinates, but only when the browser has *already* granted permission.
     *
     * This deliberately never prompts. Whether the concierge should ask for location, and what it
     * offers in return, is a product decision -- and a permission dialog appearing the first time
     * somebody types a question would quietly make that decision for us. Until it is made, this
     * uses a permission that already exists and sends nothing when there is none.
     */
    private suspend fun grantedLocation(): GPSCoordinatesInput? {
        return try {
            val permissions = window.navigator.asDynamic().permissions ?: return null
            val status = (permissions.query(json("name" to "geolocation")) as Promise<dynamic>).await()
            if (status.state != "granted") return null

            suspendCancellableCoroutine { continuation ->
                window.navigator.asDynamic().geolocation.getCurrentPosition(
                    { position: dynamic ->
                        continuation.resume(
                            GPSCoordinatesInput(
                                (position.coords.latitude as Number).toFloat(),
                                (position.coords.longitude as Number).toFloat()
                            )
                        )
                    },
                    { _: dynamic ->
                        continuation.resume(null)
                    }
                )
            }
        } catch (e: Throwable) {
            // Location is an enhancement. A browser without the permissions API, or one that
            // rejects the query, should still get an answer to the question that was asked.
            console.log("Location unavailable, continuing without it")
            null
        }
    }

    /**
     * Report that Intelligence finished. Paired one-to-one with `intelligence_queried`, so the
     * gap between the two counts is the share of questions that never got an answer — which is
     * the number worth watching for a feature that polls a model in the background.
     */
    private fun reportResponded(success: Boolean) {
        Analytics.track(
            AnalyticsEvent.INTELLIGENCE_RESPONDED,
            AnalyticsParam.SUCCESS to success
        )
    }
}
